import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Input,
  Select,
  message,
  Spin,
  Steps,
  Divider,
  Radio,
  Alert,
  Tag,
  Space,
  Typography,
  Empty,
  Upload,
} from 'antd';
import {
  cartService,
  orderService,
  addressService,
  paymentService,
  walletService,
} from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CreditCardOutlined,
  WalletOutlined,
  BankOutlined,
  QrcodeOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  RightOutlined,
  CarOutlined,
  UploadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

const Checkout = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [preOrderQrDataUrl, setPreOrderQrDataUrl] = useState(null);
  const [preOrderQrLoading, setPreOrderQrLoading] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [, setPaymentSettings] = useState(null);

  const PAYMENT_METHODS = [
    {
      value: 'cod',
      label: 'Cash on Delivery',
      icon: <BankOutlined />,
      description: 'Pay when you receive',
      color: '#52b788',
    },
    {
      value: 'wallet',
      label: 'Wallet Balance',
      icon: <WalletOutlined />,
      description:
        walletBalance !== null
          ? `Balance: NPR ${Number(walletBalance).toLocaleString()}`
          : 'Pay from your wallet',
      color: '#2e9bd6',
    },
    {
      value: 'qr',
      label: 'QR Payment',
      icon: <QrcodeOutlined />,
      description: 'Scan & pay',
      color: '#f1a134',
    },
  ];

  const stepIcons = [<EnvironmentOutlined />, <ShoppingCartOutlined />, <CreditCardOutlined />];

  const formatNPR = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };
  const DELIVERY_FEE = 199;
  const FREE_DELIVERY_THRESHOLD = 5000;

  useEffect(() => {
    const itemsParam = searchParams.get('items');
    if (itemsParam) {
      setSelectedItemIds(itemsParam.split(',').map(Number).filter(Boolean));
    }
    fetchCart();
    fetchAddresses();
    fetchPaymentSettings();
    fetchWallet();

    const user = JSON.parse(localStorage.getItem('authUser') || '{}');
    if (user.full_name || user.phone_number) {
      form.setFieldsValue({
        full_name: user.full_name || '',
        phone_number: user.phone_number || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCart = async () => {
    try {
      const response = await cartService.getCart();
      setCart(response.data || response);
    } catch (error) {
      message.error('Failed to load cart');
      navigate('/cart');
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await addressService.getAddresses();
      const data = response.data?.data || response.data || [];
      setAddresses(data);
      if (data.length > 0) setSelectedAddress(data[0].id);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await paymentService.getPaymentSettings();
      setPaymentSettings(response.data?.data || response.data);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchWallet = async () => {
    try {
      const response = await walletService.getWallet();
      setWalletBalance(response.data?.balance ?? response.data?.data?.balance ?? null);
    } catch {
      setWalletBalance(null);
    }
  };

  const handleAddAddress = async (values) => {
    try {
      setLoading(true);
      await addressService.addAddress(values);
      await fetchAddresses();
      form.resetFields();
      setStep(1);
    } catch (error) {
      message.error('Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const generatePreOrderQr = async (amount) => {
    setPreOrderQrLoading(true);
    try {
      const response = await paymentService.generateQrCode(amount);
      setPreOrderQrDataUrl(response.data?.data?.qr_code);
    } catch (error) {
      setPreOrderQrDataUrl(null);
    } finally {
      setPreOrderQrLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      message.error('Please select a delivery address');
      return;
    }
    if (paymentMethod === 'qr' && !transactionId.trim() && !paymentProofFile) {
      message.warning('Please provide a transaction ID or upload a payment screenshot.');
      return;
    }
    try {
      setLoading(true);
      const orderPayload = {
        address_id: selectedAddress,
        payment_method: paymentMethod,
      };
      if (selectedItemIds.length > 0) {
        orderPayload.cart_item_ids = selectedItemIds;
      }
      const response = await orderService.createOrder(orderPayload);
      const orders = response.data?.data || [];
      const orderId = orders[0]?.id || response.data?.id || response.id;
      const orderIds = Array.isArray(orders) && orders.length > 1 ? orders.map((o) => o.id) : null;

      if (paymentMethod === 'qr') {
        try {
          const ids = orderIds || [orderId];
          for (const oid of ids) {
            const fd = new FormData();
            if (transactionId.trim()) fd.append('transaction_id', transactionId.trim());
            if (paymentProofFile) fd.append('payment_proof_image', paymentProofFile);
            if (orderIds) {
              fd.append('payment_remarks', `Combined payment for orders: ${orderIds.join(', ')}`);
            }
            await paymentService.submitPaymentProof(oid, fd);
          }
        } catch (proofError) {
          message.warning(
            'Order created! But proof submission failed. You can submit it from your orders page.',
          );
          navigate('/orders');
          return;
        }
        message.success('Order placed with payment proof! Awaiting verification.');
        navigate('/orders');
      } else {
        if (orderIds) {
          message.success('Orders placed successfully!');
        } else {
          message.success('Order placed successfully!');
        }
        navigate(orderIds ? '/orders' : `/tracking/${orderId}`);
      }
    } catch (error) {
      const errData = error.response?.data;
      if (errData?.wallet_balance !== undefined) {
        message.error(
          `Insufficient wallet balance (NPR ${Number(errData.wallet_balance).toLocaleString()}). Required: NPR ${Number(errData.required_amount).toLocaleString()}. Choose another payment method.`,
        );
      } else {
        message.error(errData?.message || 'Failed to place order');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentMethod === 'qr' && cart) {
      const allItems = cart.items || cart.data?.items || [];
      const filtered =
        selectedItemIds.length > 0
          ? allItems.filter((item) => selectedItemIds.includes(item.id))
          : allItems;
      const getPrice = (item) => {
        const p = parseFloat(item.price_at_add);
        if (!isNaN(p)) return p;
        const p2 = parseFloat(item.price);
        if (!isNaN(p2)) return p2;
        return parseFloat(item.product_service?.price) || 0;
      };
      const sub =
        filtered.reduce((sum, item) => sum + getPrice(item) * (item.quantity || 1), 0) || 0;
      const hasService = filtered.some((item) => item.product_service?.type === 'service');
      const del = hasService || sub >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
      const tot = sub + del;
      if (tot > 0) {
        generatePreOrderQr(tot);
      }
    } else {
      setPreOrderQrDataUrl(null);
    }
  }, [paymentMethod, cart, selectedItemIds]);

  if (!cart) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const allItems = cart.items || cart.data?.items || [];
  const items =
    selectedItemIds.length > 0
      ? allItems.filter((item) => selectedItemIds.includes(item.id))
      : allItems;
  const getCartPrice = (item) => {
    const p = parseFloat(item.price_at_add);
    if (!isNaN(p)) return p;
    const p2 = parseFloat(item.price);
    if (!isNaN(p2)) return p2;
    return parseFloat(item.product_service?.price) || 0;
  };
  const subtotal =
    items.reduce((sum, item) => {
      return sum + getCartPrice(item) * (item.quantity || 1);
    }, 0) || 0;
  const hasService = items.some((item) => item.product_service?.type === 'service');
  const delivery = hasService || subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const total = subtotal + delivery;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <style>{`
        .cc-checkout-steps .ant-steps-item-process .ant-steps-item-icon {
          background: linear-gradient(135deg, #52b788, #2d5a3d);
          border-color: #2d5a3d;
        }
        .cc-checkout-steps .ant-steps-item-finish .ant-steps-item-icon {
          border-color: #52b788;
          background: #e8f5e9;
        }
        .cc-checkout-steps .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon,
        .cc-checkout-steps .ant-steps-item-finish .ant-steps-item-icon .anticon {
          color: #2d5a3d;
        }
        .cc-checkout-steps .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
          background-color: #52b788;
        }
        .cc-checkout-steps .ant-steps-item-process .ant-steps-item-title,
        .cc-checkout-steps .ant-steps-item-finish .ant-steps-item-title {
          color: #2d5a3d !important;
          font-weight: 600;
        }
      `}</style>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <Title level={2} style={{ color: '#2d5a3d', margin: 0 }}>
          Checkout
        </Title>
        <Text style={{ color: '#6b7280', marginTop: 4, display: 'block' }}>
          Review your order and complete your purchase
        </Text>
      </div>

      <Steps
        className="cc-checkout-steps"
        current={step}
        items={[
          { title: 'Address', icon: stepIcons[0] },
          { title: 'Review', icon: stepIcons[1] },
          { title: 'Payment', icon: stepIcons[2] },
        ]}
        style={{ marginBottom: 36, padding: '0 24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} md={16}>
          {step === 0 && (
            <Card
              styles={{
                header: {
                  background: 'linear-gradient(135deg, #fafff5, #f0faf0)',
                  borderBottom: '1px solid #e8f0e8',
                  borderRadius: '12px 12px 0 0',
                },
                body: { padding: 24 },
              }}
              style={{
                borderRadius: 12,
                border: '1px solid #e8f0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 16,
                  }}
                >
                  <EnvironmentOutlined />
                </div>
                <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                  Delivery Address
                </Text>
              </div>

              {addresses.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <Text
                    style={{ color: '#6b7280', fontSize: 13, display: 'block', marginBottom: 8 }}
                  >
                    Select an existing address
                  </Text>
                  <Select
                    value={selectedAddress}
                    onChange={setSelectedAddress}
                    style={{ width: '100%' }}
                    placeholder="Choose your delivery address"
                  >
                    {addresses.map((addr) => (
                      <Select.Option key={addr.id} value={addr.id}>
                        <div>
                          <Text strong>{addr.address_line1}</Text>
                          <Text style={{ color: '#6b7280', marginLeft: 4 }}>- {addr.city}</Text>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>

                  <Button
                    onClick={() => setStep(1)}
                    type="primary"
                    size="large"
                    block
                    style={{
                      marginTop: 16,
                      height: 46,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                      border: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Continue to Review <RightOutlined style={{ marginLeft: 6 }} />
                  </Button>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16,
                  padding: '12px 0',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#52b788',
                    fontSize: 14,
                  }}
                >
                  <PlusOutlined />
                </div>
                <Text style={{ color: '#2d5a3d', fontWeight: 500 }}>Add a new address</Text>
              </div>

              <Form form={form} layout="vertical" onFinish={handleAddAddress}>
                <Row gutter={16}>
                  <Col xs={12}>
                    <Form.Item
                      name="full_name"
                      label={<Text style={{ color: '#374151' }}>Full Name</Text>}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="Your full name" style={{ borderRadius: 8, height: 42 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item
                      name="phone_number"
                      label={<Text style={{ color: '#374151' }}>Phone Number</Text>}
                      rules={[
                        { required: true },
                        { pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' },
                      ]}
                    >
                      <Input
                        type="tel"
                        onKeyPress={(e) =>
                          e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()
                        }
                        placeholder="98XXXXXXXX"
                        style={{ borderRadius: 8, height: 42 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="address_line1"
                      label={<Text style={{ color: '#374151' }}>Address</Text>}
                      rules={[{ required: true }]}
                    >
                      <Input
                        placeholder="Street, house no., area"
                        style={{ borderRadius: 8, height: 42 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name="city"
                      label={<Text style={{ color: '#374151' }}>City</Text>}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="Kathmandu" style={{ borderRadius: 8, height: 42 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Button
                      htmlType="submit"
                      loading={loading}
                      block
                      style={{
                        height: 42,
                        borderRadius: 8,
                        borderColor: '#52b788',
                        color: '#2d5a3d',
                        fontWeight: 500,
                      }}
                    >
                      Save and Continue
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}

          {step === 1 && (
            <Card
              styles={{
                header: {
                  background: 'linear-gradient(135deg, #fafff5, #f0faf0)',
                  borderBottom: '1px solid #e8f0e8',
                  borderRadius: '12px 12px 0 0',
                },
                body: { padding: 0 },
              }}
              style={{
                borderRadius: 12,
                border: '1px solid #e8f0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 16,
                    }}
                  >
                    <ShoppingCartOutlined />
                  </div>
                  <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                    Order Summary
                  </Text>
                  <Tag
                    style={{
                      marginLeft: 'auto',
                      borderRadius: 20,
                      background: '#e8f5e9',
                      color: '#2d5a3d',
                      border: 'none',
                    }}
                  >
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                  </Tag>
                </div>

                {items.length === 0 ? (
                  <Empty description="No items in cart" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  items.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '14px 0',
                        borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 10,
                          background: '#f0faf0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                          color: '#52b788',
                          flexShrink: 0,
                        }}
                      >
                        <ShoppingCartOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ color: '#2d5a3d', display: 'block' }}>
                          {item.product_service?.name || item.product_name || item.name}
                        </Text>
                        <Text style={{ color: '#9ca3af', fontSize: 13 }}>
                          Qty: {item.quantity} x {formatNPR(getCartPrice(item))}
                        </Text>
                        {item.booking_date && (
                          <Text
                            style={{
                              color: '#52b788',
                              fontSize: 12,
                              display: 'block',
                              marginTop: 2,
                            }}
                          >
                            Booking: {item.booking_date}
                            {item.booking_time ? ` at ${item.booking_time}` : ''}
                          </Text>
                        )}
                      </div>
                      <Text strong style={{ color: '#2d5a3d', fontSize: 15, whiteSpace: 'nowrap' }}>
                        {formatNPR(getCartPrice(item) * item.quantity)}
                      </Text>
                    </div>
                  ))
                )}

                <Divider style={{ margin: '16px 0' }} />

                <div style={{ padding: '4px 0' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <Text style={{ color: '#6b7280' }}>Subtotal</Text>
                    <Text strong style={{ color: '#2d5a3d' }}>
                      {formatNPR(subtotal)}
                    </Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <Text style={{ color: '#6b7280' }}>
                      <CarOutlined style={{ marginRight: 6 }} />
                      Delivery Fee
                    </Text>
                    {delivery === 0 ? (
                      <Tag color="green" style={{ margin: 0, borderRadius: 20 }}>
                        Free
                      </Tag>
                    ) : (
                      <Text strong style={{ color: '#2d5a3d' }}>
                        {formatNPR(delivery)}
                      </Text>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: 12,
                      borderTop: '2px solid #2d5a3d',
                    }}
                  >
                    <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                      Total
                    </Text>
                    <Text strong style={{ fontSize: 18, color: '#2d5a3d' }}>
                      {formatNPR(total)}
                    </Text>
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  background: '#fafff5',
                  borderTop: '1px solid #e8f0e8',
                  borderRadius: '0 0 12px 12px',
                  display: 'flex',
                  gap: 12,
                }}
              >
                <Button
                  onClick={() => setStep(0)}
                  style={{ borderRadius: 8, height: 44, minWidth: 100 }}
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep(2)}
                  type="primary"
                  size="large"
                  style={{
                    flex: 1,
                    height: 44,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    border: 'none',
                    fontWeight: 600,
                  }}
                >
                  Continue to Payment <RightOutlined style={{ marginLeft: 6 }} />
                </Button>
              </div>
            </Card>
          )}

          {step === 2 && (
            <Card
              styles={{
                header: {
                  background: 'linear-gradient(135deg, #fafff5, #f0faf0)',
                  borderBottom: '1px solid #e8f0e8',
                  borderRadius: '12px 12px 0 0',
                },
                body: { padding: 24 },
              }}
              style={{
                borderRadius: 12,
                border: '1px solid #e8f0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 16,
                  }}
                >
                  <CreditCardOutlined />
                </div>
                <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                  Payment Method
                </Text>
              </div>

              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {PAYMENT_METHODS.map((method) => (
                    <Card
                      key={method.value}
                      hoverable
                      onClick={() => setPaymentMethod(method.value)}
                      style={{
                        borderRadius: 10,
                        border:
                          paymentMethod === method.value
                            ? `2px solid ${method.color}`
                            : '1px solid #e8f0e8',
                        cursor: 'pointer',
                        background: paymentMethod === method.value ? '#fafff5' : '#fff',
                        transition: 'all 0.2s ease',
                      }}
                      styles={{ body: { padding: '14px 18px' } }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <Radio value={method.value} />
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background:
                              paymentMethod === method.value ? `${method.color}15` : '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20,
                            color: paymentMethod === method.value ? method.color : '#9ca3af',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                        >
                          {method.icon}
                        </div>
                        <div>
                          <Text
                            strong
                            style={{
                              color: paymentMethod === method.value ? '#2d5a3d' : '#374151',
                              transition: 'color 0.2s ease',
                            }}
                          >
                            {method.label}
                          </Text>
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>{method.description}</div>
                        </div>
                        {paymentMethod === method.value && (
                          <CheckCircleOutlined
                            style={{ marginLeft: 'auto', color: method.color, fontSize: 18 }}
                          />
                        )}
                      </div>
                    </Card>
                  ))}
                </Space>
              </Radio.Group>

              {paymentMethod === 'qr' && (
                <div
                  style={{
                    marginTop: 20,
                    padding: 20,
                    background: 'linear-gradient(135deg, #fef4e3, #fde6c8)',
                    border: '1px solid #f6d9a8',
                    borderRadius: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <SafetyCertificateOutlined style={{ color: '#f1a134', fontSize: 18 }} />
                    <Text strong style={{ color: '#e08a10' }}>
                      Scan & Pay
                    </Text>
                  </div>

                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {preOrderQrLoading ? (
                      <div style={{ padding: 40 }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
                        <div style={{ marginTop: 12, color: '#6b7280' }}>Generating QR code...</div>
                      </div>
                    ) : preOrderQrDataUrl ? (
                      <img
                        src={preOrderQrDataUrl}
                        alt="Payment QR Code"
                        style={{ width: 260, height: 260, borderRadius: 12 }}
                      />
                    ) : (
                      <Alert
                        message="Unable to generate QR code. Please try again."
                        type="warning"
                        showIcon
                      />
                    )}
                    <div style={{ marginTop: 12 }}>
                      <Text style={{ color: '#6b7280', fontSize: 13 }}>Total Amount</Text>
                      <div style={{ fontSize: 24, fontWeight: 700, color: '#2d5a3d' }}>
                        {formatNPR(total)}
                      </div>
                    </div>
                  </div>

                  <Divider style={{ borderColor: '#f6d9a8', margin: '12px 0' }}>
                    Payment Details
                  </Divider>

                  <Form layout="vertical">
                    <Form.Item
                      label={
                        <Text strong style={{ color: '#374151' }}>
                          Transaction ID
                        </Text>
                      }
                    >
                      <Input
                        placeholder="Enter payment transaction ID"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        style={{ borderRadius: 8, height: 42 }}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <Text strong style={{ color: '#374151' }}>
                          Payment Screenshot
                        </Text>
                      }
                    >
                      <Upload
                        beforeUpload={(file) => {
                          setPaymentProofFile(file);
                          return false;
                        }}
                        showUploadList={false}
                        accept="image/*"
                      >
                        <Button
                          icon={<UploadOutlined />}
                          block
                          style={{ borderRadius: 8, height: 42 }}
                        >
                          {paymentProofFile ? paymentProofFile.name : 'Upload Screenshot'}
                        </Button>
                      </Upload>
                      <Text
                        style={{ color: '#9ca3af', fontSize: 12, marginTop: 4, display: 'block' }}
                      >
                        Upload a screenshot of your payment confirmation
                      </Text>
                    </Form.Item>
                  </Form>
                </div>
              )}

              <Divider />

              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  onClick={() => setStep(1)}
                  style={{ borderRadius: 8, height: 48, minWidth: 100 }}
                >
                  Back
                </Button>
                <Button
                  onClick={handlePlaceOrder}
                  type="primary"
                  loading={loading}
                  size="large"
                  style={{
                    flex: 1,
                    height: 48,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  <CheckCircleOutlined style={{ marginRight: 8 }} />
                  {paymentMethod === 'qr' ? 'Submit & Place Order' : 'Place Order'}
                </Button>
              </div>
            </Card>
          )}
        </Col>

        <Col xs={24} md={8}>
          <Card
            styles={{
              header: {
                background: 'linear-gradient(135deg, #2d5a3d, #3d7a4d)',
                borderRadius: '12px 12px 0 0',
                borderBottom: 'none',
              },
              body: { padding: 20 },
            }}
            title={
              <Text strong style={{ color: '#fff', fontSize: 15 }}>
                Order Total
              </Text>
            }
            style={{
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 4px 12px rgba(45,90,61,0.15)',
              position: 'sticky',
              top: 90,
            }}
          >
            <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e8f0e8' }}>
              {items.length === 0 ? (
                <Text style={{ color: '#9ca3af' }}>No items</Text>
              ) : (
                items.map((item, i) => (
                  <div
                    key={i}
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}
                  >
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <Text
                        style={{
                          color: '#4b5563',
                          fontSize: 13,
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.product_service?.name || item.product_name || item.name}
                        <Text style={{ color: '#9ca3af' }}> x{item.quantity}</Text>
                      </Text>
                      {item.booking_date && (
                        <Text style={{ color: '#52b788', fontSize: 11, display: 'block' }}>
                          {item.booking_date}
                          {item.booking_time ? ` ${item.booking_time}` : ''}
                        </Text>
                      )}
                    </div>
                    <Text
                      style={{ color: '#374151', fontSize: 13, fontWeight: 500, marginLeft: 8 }}
                    >
                      {formatNPR(getCartPrice(item) * item.quantity)}
                    </Text>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
              <Text style={{ color: '#6b7280' }}>Subtotal</Text>
              <Text strong style={{ color: '#2d5a3d' }}>
                {formatNPR(subtotal)}
              </Text>
            </div>
            <div
              style={{
                marginBottom: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text style={{ color: '#6b7280' }}>Delivery</Text>
              {delivery === 0 ? (
                <Tag color="green" style={{ margin: 0, borderRadius: 20, fontSize: 11 }}>
                  Free
                </Tag>
              ) : (
                <Text strong style={{ color: '#2d5a3d' }}>
                  {formatNPR(delivery)}
                </Text>
              )}
            </div>

            <Divider style={{ margin: '12px 0', borderColor: '#e8f0e8' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                Total
              </Text>
              <Text strong style={{ fontSize: 20, color: '#2d5a3d' }}>
                {formatNPR(total)}
              </Text>
            </div>

            {delivery === 0 && subtotal > 0 && (
              <div
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#f0faf0',
                  borderRadius: 8,
                }}
              >
                <SafetyCertificateOutlined style={{ color: '#52b788', marginRight: 6 }} />
                <Text style={{ color: '#2d5a3d', fontSize: 12 }}>
                  {hasService
                    ? 'Free delivery on service orders'
                    : 'Free delivery on orders over NPR 5,000'}
                </Text>
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                padding: '10px 14px',
                background: '#f9fafb',
                borderRadius: 8,
              }}
            >
              <Text
                style={{
                  color: '#6b7280',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <SafetyCertificateOutlined style={{ color: '#52b788' }} />
                Secure checkout. Your data is protected.
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Checkout;
